import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Quote, TrendingDown, Gauge, Users2, ArrowRight, Linkedin } from 'lucide-react';

const Founder = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const achievements = [
    { icon: TrendingDown, stat: '40%', label: 'Avg. Cost Reduction' },
    { icon: Gauge, stat: '2x', label: 'Productivity Boost' },
    { icon: Users2, stat: '200+', label: 'Companies Helped' }
  ];

  return (
    <section id="founder" ref={ref} className="relative py-32 bg-gradient-to-br from-slate-950 via-blue-950 to-slate-950 overflow-hidden">
      {/* Ambient background */}
      <div className="absolute inset-0 opacity-[0.15]">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 2px 2px, rgba(234, 179, 8, 0.5) 1px, transparent 0)`,
          backgroundSize: '64px 64px'
        }} />
      </div>

      {/* Large editorial background word */}
      <div className="absolute top-8 left-0 right-0 text-center pointer-events-none select-none">
        <span className="text-[10rem] lg:text-[16rem] font-bold text-white/[0.02] tracking-tighter leading-none">
          LEADERSHIP
        </span>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* LEFT — Creative Photo Composition */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative flex justify-center"
          >
            <div className="relative w-full max-w-md">
              {/* Rotating dashed ring */}
              {/* Static decorative rings (no continuous animation for performance) */}
              <div className="absolute inset-0 -m-6 rounded-full border-2 border-dashed border-yellow-400/30" />
              <div className="absolute inset-0 -m-12 rounded-full border border-blue-400/20" />

              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-tr from-yellow-500/40 to-blue-500/30 rounded-full blur-2xl" />

              {/* Photo in circular frame */}
              <div className="relative aspect-square rounded-full overflow-hidden border-4 border-yellow-400/40 shadow-2xl bg-gradient-to-br from-slate-800 to-slate-900">
                <img
                  src="https://customer-assets.emergentagent.com/job_admin-flex-1/artifacts/23j1hq7y_ChatGPT%20Image%20Jun%2023%2C%202026%2C%2006_05_26%20PM.png"
                  alt="Afftab Ahmed, Founder & Director of Flexdesk UK"
                  className="w-full h-full object-cover"
                  data-testid="founder-image"
                />
              </div>

              {/* Floating experience badge */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="absolute top-6 -left-4 lg:-left-10 bg-white rounded-2xl px-5 py-4 shadow-2xl"
              >
                <div className="text-3xl font-bold bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text text-transparent leading-none">13+</div>
                <div className="text-xs font-semibold text-slate-600 mt-1">Years<br/>Experience</div>
              </motion.div>

              {/* Floating LinkedIn / signature chip */}
              <motion.a
                href="https://www.linkedin.com/in/afftab-ahmed-a33514143/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="founder-linkedin-link"
                initial={{ opacity: 0, y: 20 }}
                animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                whileHover={{ scale: 1.05 }}
                className="absolute bottom-8 -right-3 lg:-right-8 bg-gradient-to-br from-yellow-500 to-yellow-600 rounded-2xl px-5 py-4 shadow-2xl flex items-center gap-3 cursor-pointer"
              >
                <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
                  <Linkedin className="w-5 h-5 text-yellow-400" />
                </div>
                <div>
                  <div className="text-sm font-bold text-slate-900 leading-none">Afftab Ahmed</div>
                  <div className="text-xs text-slate-800 mt-0.5">View LinkedIn Profile</div>
                </div>
              </motion.a>
            </div>
          </motion.div>

          {/* RIGHT — Content */}
          <div className="lg:col-span-7 lg:pl-8">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={isInView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="inline-block px-4 py-2 bg-yellow-500/20 text-yellow-300 rounded-full text-sm font-semibold mb-6 border border-yellow-400/30">
                MEET THE FOUNDER
              </div>

              {/* Signature-style name */}
              <h2 className="text-5xl lg:text-7xl font-bold text-white mb-2 tracking-tight">
                Afftab <span className="bg-gradient-to-r from-yellow-400 via-yellow-300 to-yellow-500 bg-clip-text text-transparent">Ahmed</span>
              </h2>
              <div className="flex items-center gap-3 mb-8">
                <div className="h-px w-12 bg-yellow-400" />
                <p className="text-lg text-slate-300 font-medium tracking-wide uppercase">Founder & Director</p>
              </div>

              {/* Quote */}
              <div className="relative pl-8 mb-10">
                <Quote className="absolute top-0 left-0 w-6 h-6 text-yellow-400/50" />
                <p className="text-xl lg:text-2xl text-slate-200 font-light leading-relaxed">
                  With over <span className="text-yellow-400 font-semibold">13 years of hands-on expertise</span>,
                  I&apos;ve helped businesses of every size <span className="text-yellow-400 font-semibold">slash operational costs</span> and
                  <span className="text-yellow-400 font-semibold"> unlock new levels of efficiency and productivity</span>. At Flexdesk UK,
                  we turn administrative burdens into your competitive edge.
                </p>
              </div>

              {/* Achievement stats */}
              <div className="grid grid-cols-3 gap-4 mb-10">
                {achievements.map((a, i) => {
                  const Icon = a.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
                      transition={{ duration: 0.5, delay: 0.5 + i * 0.1 }}
                      className="p-5 bg-white/5 rounded-2xl border border-white/10 hover:border-yellow-400/40 hover:bg-white/10 transition-all duration-300"
                    >
                      <Icon className="w-6 h-6 text-yellow-400 mb-3" />
                      <div className="text-3xl font-bold text-white leading-none">{a.stat}</div>
                      <div className="text-xs text-slate-400 mt-2">{a.label}</div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Founder;
